<script lang="ts" setup>
import type { MaterialSymbolName } from '@shared/types/materialSymbolNames';

export type IconColor =
  | 'neutral'
  | 'neutral-muted'
  | 'disabled'
  | 'primary'
  | 'error'
  | 'on-fill'
  | 'warning'
  | 'info'
  | 'community'
  | 'inherit';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'inherit';

withDefaults(
  defineProps<{
    name: MaterialSymbolName;
    alt?: string;
    color?: IconColor;
    size?: IconSize;
    ghost?: boolean;
  }>(),
  {
    color: 'inherit',
    size: 'md',
    ghost: true,
  },
);
</script>

<template>
  <!-- `relative` anchors the absolutely positioned `sr-only` alt text here -->
  <span class="relative" :class="{ 'inline-block rounded-md border bg-muted p-2x': !ghost }">
    <i
      translate="no"
      class="material-symbols-outlined base-icon"
      :class="{
        [size]: true,
        'text-accent-neutral': color === 'neutral',
        'text-accent-neutral-muted': color === 'neutral-muted',
        'text-accent-neutral-disabled': color === 'disabled',
        'text-accent-primary': color === 'primary',
        'text-accent-error': color === 'error',
        'text-accent-on-fill': color === 'on-fill',
        'text-accent-warning': color === 'warning',
        'text-accent-info': color === 'info',
        'text-accent-community': color === 'community',
      }"
      aria-hidden="true"
    >
      {{ name }}
    </i>
    <span v-if="alt" class="sr-only">{{ alt }}</span>
  </span>
</template>

<style scoped>
.base-icon {
  line-height: inherit;
  vertical-align: bottom;
  user-select: none;
  color: inherit;
  font-variation-settings: 'FILL' 0;

  &.xs {
    font-size: 14px;
  }

  &.sm {
    font-size: 16px;
  }

  &.md {
    font-size: 18px;
  }

  &.lg {
    font-size: 20px;
  }

  &.xl {
    font-size: 24px;
  }

  &.\32xl {
    font-size: 32px;
  }

  &.inherit {
    font-size: inherit;
  }
}
</style>
